// The comments section of ANY record (Work screen, 2026-10-21): the thread
// (oldest first), a composer with @mentions of people, and resolve. Generic:
// it takes the record's type and id from the section context and knows no
// type by name — a mention's candidates are the records of the type the
// mention contract names (`person`, monad/notifications.py MENTION_TYPES),
// found by their kenning.
//
// Mentioning the tenant's AGENT is a seam, not a feature yet. Its label is
// data: the agent member's display name, served on GET /daimon/capability
// (`agent.name` — "Sentinel" in Sentinel's tenants), never spelled here.
// `registerAgentMention(handler)` lets a host (the agent runtime) take the
// mention over — the handler is given the record and the text and invokes a
// run on the record's thread. Until one is registered the composer shows
// "@<name> — coming soon", disabled; a tenant without an agent shows nothing.
import { useMemo, useRef, useState, type ComponentType, type KeyboardEvent } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { monadClient, type CommentData } from '../api/client'
import { typeQuery } from '../api/typeMetadata'
import { Button } from '../widgets/Button'
import { RichText } from '../widgets/richText'
import { registerRecordSections } from './sections/registry'
import { activeMentionQuery, insertMention, mentionRuns, mentionsInBody, type PickedMention } from './mentions'

/** The mention contract's person type (the server notifies its login). */
const MENTION_TYPE = 'person'

export interface AgentMentionHandler {
  /** Invoke the agent on this record's thread with the person's text (``agentName``: its served name). */
  (args: { slug: string; typeId: string; recordId: string; text: string; agentName: string }): Promise<void>
}
let agentMention: AgentMentionHandler | null = null
/** The @Agent seam: a host registers the handler that runs the agent. */
export function registerAgentMention(handler: AgentMentionHandler | null): void {
  agentMention = handler
}

/** What the agent host shows beside the thread: under a comment (its run's change cards), and with
 *  ``comment`` null after the thread (a run still working). */
export type AgentCommentExtrasComponent = ComponentType<{
  slug: string; typeId: string; recordId: string; comment: CommentData | null
}>
let AgentExtras: AgentCommentExtrasComponent | null = null
/** The agent host's render seam (records/agentMention.tsx). */
export function registerAgentCommentExtras(component: AgentCommentExtrasComponent | null): void {
  AgentExtras = component
}

function when(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
}

export function CommentsSection({ slug, typeId, recordId }: { slug: string; typeId: string; recordId: string }) {
  const queryClient = useQueryClient()
  const key = ['comments', slug, recordId]
  const { data: comments, error } = useQuery({
    queryKey: key,
    queryFn: () => monadClient.listComments(slug, typeId, recordId),
    staleTime: 15_000,
  })
  // Names: the authors' persons (by their login, person.member) and the
  // mentioned persons (by id) — ONE chips read for the thread.
  const authorIds = useMemo(
    () => [...new Set((comments ?? []).map((c) => c.created_by?.id).filter((x): x is string => !!x))],
    [comments],
  )
  const { data: authors } = useQuery({
    queryKey: ['comment-authors', slug, authorIds.join(',')],
    enabled: authorIds.length > 0,
    queryFn: () => monadClient.queryRecords(slug, MENTION_TYPE, {
      filter: { attribute: 'member', op: 'in', value: authorIds }, projection: ['member'], limit: 200 }),
    staleTime: 60_000,
  })
  const authorName = useMemo(() => {
    const m = new Map<string, string>()
    for (const r of authors ?? []) {
      const ref = r.values?.member as { target_record_id?: string } | Array<{ target_record_id?: string }> | undefined
      const id = Array.isArray(ref) ? ref[0]?.target_record_id : ref?.target_record_id
      if (id) m.set(id, r.kenning)
    }
    return m
  }, [authors])
  const mentionedIds = useMemo(
    () => [...new Set((comments ?? []).flatMap((c) => (c.mentions ?? []).map((x) => x.id)))],
    [comments],
  )
  const { data: mentionChips } = useQuery({
    queryKey: ['chips', slug, mentionedIds.join(',')],
    enabled: mentionedIds.length > 0,
    queryFn: () => monadClient.getChips(slug, mentionedIds),
    staleTime: 60_000,
  })

  const [body, setBody] = useState('')
  const [picked, setPicked] = useState<PickedMention[]>([])
  const [caret, setCaret] = useState(0)
  const [highlight, setHighlight] = useState(0)
  const [sending, setSending] = useState(false)
  const [sendError, setSendError] = useState<string | null>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  const { data: capability } = useQuery({
    queryKey: ['daimon-capability', slug],
    queryFn: () => monadClient.getDaimonCapability(slug),
    staleTime: 5 * 60_000,
  })
  const agentName = (capability as { agent?: { name?: string } | null } | undefined)?.agent?.name
  const mention = activeMentionQuery(body, caret)
  // A person is found by what names it: its type's kenning path (served).
  const { data: mentionType } = useQuery(typeQuery(slug, MENTION_TYPE))
  const nameAttr = ((mentionType as { kenning_path?: string[] } | undefined)?.kenning_path ?? [])[0]
  const { data: candidates } = useQuery({
    queryKey: ['mention-candidates', slug, nameAttr ?? null, mention?.query ?? null],
    enabled: mention !== null && !!nameAttr,
    queryFn: () => monadClient.queryRecords(slug, MENTION_TYPE, {
      ...(mention && mention.query ? { filter: { attribute: nameAttr!, op: 'contains', value: mention.query } } : {}),
      sorts: [{ field: nameAttr!, direction: 'asc' }], limit: 8,
    }),
    staleTime: 30_000,
  })
  // The agent is mentioned like a person (owner, 2026-10-23: no separate @Sentinel button): its served
  // name leads the suggestions when it matches what is typed.
  const AGENT_OPTION = '__agent__'
  const agentMatches = !!agentName && !!agentMention && mention !== null
    && agentName.toLowerCase().startsWith((mention.query ?? '').toLowerCase())
  const options = mention
    ? [...(agentMatches ? [{ record_id: AGENT_OPTION, kenning: agentName! }] : []), ...(candidates ?? [])]
    : []

  function pick(c: { record_id: string; kenning: string }) {
    if (!mention) return
    const next = insertMention(body, mention.start, caret, c.kenning)
    setBody(next.text)
    setCaret(next.caret)
    if (c.record_id !== AGENT_OPTION) setPicked((p) => [...p, { id: c.record_id, name: c.kenning }])
    requestAnimationFrame(() => {
      inputRef.current?.focus()
      inputRef.current?.setSelectionRange(next.caret, next.caret)
    })
  }

  async function send() {
    const text = body.trim()
    if (!text || sending) return
    // naming the agent (@<its name>) asks it: the host posts the comment and runs the agent on it
    if (agentName && agentMention && text.includes(`@${agentName}`)) return askAgent()
    setSending(true)
    setSendError(null)
    try {
      await monadClient.addComment(slug, typeId, recordId, { body: text, mentions: mentionsInBody(text, picked) })
      setBody('')
      setPicked([])
      await queryClient.invalidateQueries({ queryKey: key })
      queryClient.invalidateQueries({ queryKey: ['timeline', slug, recordId] })
    } catch (e) {
      setSendError(e instanceof Error ? e.message : 'Could not post the comment.')
    } finally {
      setSending(false)
    }
  }

  /** The mention: the host posts the person's comment and runs the agent; the thread then re-reads. */
  async function askAgent() {
    if (!agentMention || !agentName || sending) return
    setSending(true)
    setSendError(null)
    try {
      await agentMention({ slug, typeId, recordId, text: body.trim(), agentName })
      setBody('')
      setPicked([])
      await queryClient.invalidateQueries({ queryKey: key })
      queryClient.invalidateQueries({ queryKey: ['agent-runs', slug, recordId] })
    } catch (e) {
      setSendError(e instanceof Error ? e.message : `Could not ask ${agentName}.`)
    } finally {
      setSending(false)
    }
  }

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (options.length > 0) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight((h) => (h + 1) % options.length); return }
      if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight((h) => (h - 1 + options.length) % options.length); return }
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); pick(options[Math.min(highlight, options.length - 1)]); return }
    }
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); void send() }
  }

  const nameOf = (c: CommentData): string => {
    const by = c.created_by
    if (by?.type === 'agent') return agentName ?? 'Agent'
    return (by?.id && authorName.get(by.id)) || 'Someone'
  }
  const mentionNames = (c: CommentData) => (c.mentions ?? []).map((m) => mentionChips?.chips[m.id]?.kenning ?? '')

  return (
    <div className="min-w-0 py-4" data-testid="comments-section">
      {error ? (
        <p role="alert" className="text-sm text-destructive">Could not load the comments.</p>
      ) : (comments ?? []).length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="comments-empty">No comments yet. Mention someone with @ to tell them.</p>
      ) : (
        <ul className="space-y-3" data-testid="comments-list">
          {(comments ?? []).map((c) => (
            <li key={c.comment_id} data-testid={`comment-${c.comment_id}`} className="text-sm">
              <div className="flex items-baseline gap-2">
                <span className="font-medium text-foreground">{nameOf(c)}</span>
                {/* WHO wrote it is the actor's TYPE, never the name alone (a person
                    could carry the agent's name): the agent gets its own marker */}
                {c.created_by?.type === 'agent' && (
                  <span data-testid="comment-agent-marker"
                    className="rounded-sm bg-primary/10 px-1 py-px text-2xs font-medium uppercase tracking-wide text-primary">agent</span>
                )}
                <span className="text-xs text-muted-foreground">{when(c.created_at)}</span>
                {c.resolved_at && <span className="text-xs text-muted-foreground">· resolved</span>}
              </div>
              {c.created_by?.type === 'agent' && mentionNames(c).length === 0 ? (
                // the agent writes Markdown; a person's comment stays exactly as typed
                <RichText text={c.body} className="mt-0.5 text-foreground" />
              ) : (
              <p className="mt-0.5 whitespace-pre-wrap break-words text-foreground">
                {mentionRuns(c.body, mentionNames(c)).map((run, i) =>
                  run.mention
                    ? <span key={i} data-testid="comment-mention" className="rounded-sm bg-primary/10 px-0.5 font-medium text-primary">{run.text}</span>
                    : <span key={i}>{run.text}</span>)}
              </p>
              )}
              {AgentExtras && <AgentExtras slug={slug} typeId={typeId} recordId={recordId} comment={c} />}
            </li>
          ))}
        </ul>
      )}

      {AgentExtras && <AgentExtras slug={slug} typeId={typeId} recordId={recordId} comment={null} />}

      <div className="relative mt-4 rounded-md border border-border bg-background focus-within:ring-1 focus-within:ring-ring">
        <textarea
          ref={inputRef}
          data-testid="comment-input"
          aria-label="Comment"
          rows={3}
          value={body}
          placeholder="Leave a comment, or @mention someone…"
          onChange={(e) => { setBody(e.target.value); setCaret(e.target.selectionStart ?? e.target.value.length); setHighlight(0) }}
          onSelect={(e) => setCaret((e.target as HTMLTextAreaElement).selectionStart ?? 0)}
          onKeyDown={onKeyDown}
          className="block w-full resize-none rounded-md bg-transparent px-3 py-2 text-sm outline-none placeholder:text-muted-foreground"
        />
        {options.length > 0 && (
          <ul role="listbox" aria-label="Mention someone" data-testid="mention-options"
            className="absolute bottom-full left-2 z-50 mb-1 min-w-[14rem] rounded-float border border-border bg-popover py-1 shadow-float">
            {options.map((o, i) => (
              <li key={o.record_id} role="option" aria-selected={i === highlight}>
                <Button variant="menuItem" size="menu" data-testid={`mention-option-${o.record_id}`}
                  className={`w-full ${i === highlight ? 'bg-muted' : ''}`}
                  onMouseDown={(e) => { e.preventDefault(); pick(o) }}>
                  {o.kenning}
                </Button>
              </li>
            ))}
          </ul>
        )}
        <div className="flex items-center gap-2 border-t border-border px-2 py-1.5">
          {sendError && <span role="alert" className="text-xs text-destructive">{sendError}</span>}
          <Button variant="primary" size="sm" data-testid="comment-send" className="ml-auto"
            disabled={sending || !body.trim()} onClick={() => void send()}>
            {sending ? 'Posting…' : 'Comment'}
          </Button>
        </div>
      </div>
    </div>
  )
}

registerRecordSections({
  name: 'comments', order: 5, purpose: 'The comment thread of the current record, with @mentions of people.',
  sections: (context) => context.typeId
    ? [{ id: 'comments', label: 'Comments', peek: 'open', render: () =>
        <CommentsSection slug={context.slug} typeId={context.typeId!} recordId={context.recordId} /> }]
    : [],
})
